import { defineSemanticTokens } from '@chakra-ui/react'

/**
 * Light-mode semantic tokens for the dark package.
 * These live under the `light.*` namespace to avoid conflict with dark tokens,
 * since Chakra v3 `_light`/_dark` conditions cannot be used reliably
 * (Storybook adds class="light" based on system preference, breaking dark stories).
 *
 * Only the tokens used by TypeaheadSearch are defined here for now.
 */
export const lightSemanticColors = defineSemanticTokens.colors({
  light: {
    bg: {
      // ─── Sidebar (always dark — same values as dark mode) ─────────────────
      sidebar: { value: '#163580' },
      'sidebar-hover': { value: '#1E2F45' },
      'sidebar-active': { value: '#1C283F' },
      'sidebar-account': { value: '#9DABC8' },
      // Sidebar user initials circle. Legacy.tokens.json → bg/user-badge.
      'user-badge': { value: '#31405E' },
      // ─── Badge / Notification ─────────────────────────────────────────────
      'badge-nav': { value: '#14B8A6' },
      'badge-button': { value: '#CBD5E1' },
      'badge-incident': { value: '{colors.red.500}' },
      'notification-dot': { value: '#14B8A6' },
      // Loading placeholder fill (e.g. TopBar count skeleton). Chakra v1's
      // default Skeleton end color (gray.400) so the pulse reads softly.
      skeleton: { value: '#A0AEC0' },
      app: { value: '{colors.legacy.appBg}' },
      input: { value: '#CFD6E2' },
      card: { value: '{colors.white}' },
      topbar: { value: '{colors.white}' },
      modal: { value: '#f0f1f3' },
      'card-elevated': { value: '{colors.white}' },
      'segmented-track': { value: '#cfd6e2' },
      'segment-active': { value: '#ffffff' },
      'segment-hover': { value: '#f0f1f5' },
      'toggle-track': { value: '{colors.legacy.toggleTrack}' },
      'section-header': { value: '{colors.ghostProtocol.50}' },
      label: { value: '{colors.overwatch.900}' },
      'label-archived': { value: '{colors.overwatch.950}' },
      'label-tooltip': { value: '{colors.darkBlue.800}' },
      overlay: { value: 'rgba(0, 0, 0, 0.35)' },
      // Popover surfaces stay dark navy even in Legacy (Legacy.tokens.json).
      popover: { value: '{colors.duskBlue.700}' },
      // Current Figma bg/tooltip reuses the Legacy UI/Grey/300 swatch.
      tooltip: { value: '{colors.light.bg.sidebar-account}' },
      // Calendar selected-day fill — dawn-blue in both modes per the design
      // library variable description (not yet in the Legacy token export).
      'calendar-selected': { value: '{colors.dawnBlue.300}' },
      // ─── Table ────────────────────────────────────────────────────────────
      // Container uses bg.card (white) — defined above.
      // Row sits on white; hover lifts to a faint warm-gray tint.
      'table-row': { value: '{colors.white}' },
      'table-row-hover': { value: '#EDF2F7' },
      'table-row-alt': { value: '#F8F9FC' },
      'table-row-selected': { value: '#EDF2F7' },
      // Active pagination bubble: light dawnBlue tint so dark text is legible.
      'tab-active': { value: '#47546D' },
      callout: { value: '{colors.duskBlue.50}' },
      'checkbox-on': { value: '{colors.duskBlue.700}' },
      'action-bar': { value: '#123154' },
      'action-bar-hover': { value: '#265B97' },
      'action-bar-active': { value: '#306EB5' },
      chip: { value: '#374151' },
      'chip-secondary': { value: '{colors.ghostProtocol.100}' },
      'chip-in-field': { value: '#EDF2F8' },
      'alert-success': { value: '#ECFDF5' },
      'alert-warning': { value: '#FFEAD9' },
    },
    text: {
      // Over the media overlay scrim, dark in both schemes — stays light.
      // Provisional pending Figma variables.
      'on-overlay': { value: '{colors.white}' },
      'on-overlay-muted': { value: 'rgba(255, 255, 255, 0.4)' },
      // ─── Nav (always dark — same values as dark mode) ─────────────────────
      'nav-default': { value: '#FFFFFF' },
      'nav-active': { value: '#14B8A6' },
      // ─────────────────────────────────────────────────────────────────────
      'badge-nav': { value: '#F0F1F5' },
      'badge-button': { value: '{colors.darkBlue.900}' },
      'badge-incident': { value: '{colors.darkBlue.50}' },
      'on-accent': { value: '#FFFFFF' },
      'on-danger': { value: '#FFFFFF' },
      primary: { value: '{colors.darkBlue.700}' },
      secondary: { value: '{colors.darkBlue.300}' },
      tertiary: { value: '{colors.ghostProtocol.500}' },
      disabled: { value: '#CBD5E1' },
      // Day-number text on a selected calendar day — dark navy in both modes.
      'calendar-selected': { value: '{colors.darkBlue.900}' },
      'search-placeholder': { value: '{colors.darkBlue.200}' },
      'action-bar-label': { value: '#FFFFFF' },
      'action-bar-action': { value: '#FFFFFF' },
      label: { value: '{colors.overwatch.400}' },
      'label-archived': { value: '{colors.overwatch.700}' },
      'label-tooltip': { value: '{colors.darkBlue.50}' },
      link: { value: '{colors.accent.blue}' },
      // Legacy brand blue for incident-reference links (fixed in both modes).
      'incident-link': { value: '{colors.legacy.brandBlue}' },
      // Active pagination bubble text (dark teal on light blue).
      'tab-active': { value: '{colors.darkBlue.600}' },
      'chip-label': { value: '#E8EDF2' },
      'chip-secondary': { value: '{colors.ghostProtocol.800}' },
      'chip-in-field': { value: '{colors.darkBlue.800}' },
    },
    border: {
      search: { value: '{colors.ghostProtocol.200}' },
      input: { value: '#D1D5DB' },
      'input-hover': { value: '{colors.ghostProtocol.300}' },
      // Ring around TODAY's date when it is not the selected day.
      'calendar-today': { value: '{colors.dawnBlue.400}' },
      // Unchecked checkbox outline — Legacy.tokens.json border/checkbox-off
      // aliases {interactive.tertiary-border} (#E2E8F0 in Legacy).
      'checkbox-off': { value: '#E2E8F0' },
      'section-divider': { value: '{colors.darkBlue.500}' },
      'menu-divider': { value: '{colors.darkBlue.300}' },
      'label-tooltip': { value: '{colors.darkBlue.600}' },
      default: { value: '#E2E8F0' },
      topbar: { value: '#E2E8F0' },
      // Subtle divider between header, rows, and pagination. Matches the
      // rebrand palette (web-app theme.tsx border.subtle.light and the
      // Legacy.tokens.json snapshot, hand-synced from Figma).
      subtle: { value: '#CFD6E2' },
      // Heavier gray (design library: border/strong, ghostprotocol/600 on dark).
      strong: { value: '#CBD5E1' },
      'tab-toggle': { value: '#E2E8F0' },
      'action-bar': { value: '#1B4474' },
      'input-focus': { value: '{colors.dawnBlue.600}' },
      glow: { value: '{colors.dawnBlue.400}' },
    },
    fg: {
      'toggle-knob': { value: '{colors.darkBlue.900}' },
    },
    icon: {
      primary: { value: '{colors.white}' },
      secondary: { value: '#94A3B8' },
      // Figma: icon/tertiary (Legacy → UI/Grey/900). See the dark counterpart.
      tertiary: { value: '#0c121d' },
      active: { value: '#14B8A6' },
      danger: { value: '{colors.light.interactive.danger-hover}' },
      disabled: { value: '#D1D5DB' },
      'checkbox-on': { value: '{colors.darkBlue.50}' },
      // ─── Incident action bar (legacy pre-rebrand blues, not in design token JSON) ──
      'action-default': { value: '#3d7fce' },
      'action-hover': { value: '#5a6c9a' },
      'action-selected': { value: '{colors.white}' },
      'action-disabled': { value: '#a0b4d8' },
    },
    status: {
      'workflow-draft': { value: '{colors.darkBlue.300}' },
      'workflow-published': { value: '{colors.green.400}' },
      'workflow-archived': { value: '{colors.ghostProtocol.500}' },
      'active-bg': { value: '#EEF6FF' },
      'active-text': { value: '#3D7FCE' },
      'info-bg': { value: '#DBEAFE' },
      info: { value: '{colors.dawnBlue.600}' },
      'info-text': { value: '{colors.dawnBlue.900}' },
      'in-progress-bg': { value: '#E1FFFE' },
      'in-progress-text': { value: '#16B3B0' },
      'resolved-bg': { value: '#DAFFF1' },
      'resolved-text': { value: '#12B779' },
      success: { value: '#22C55E' },
      'success-bg': { value: '#DCFCE7' },
      'success-icon': { value: '#16A34A' },
      'success-text': { value: '#3D8B25' },
      warning: { value: '#ED6100' },
      'warning-bg': { value: '#FEF3C7' },
      'warning-text': { value: '#993E00' },
      error: { value: '#DC2626' },
      'error-bg': { value: '#FEE2E2' },
      'error-text': { value: '{colors.red.500}' },
      // Count badge in the top navigation bar (navy fill, white text).
      'incident-badge-topbar': { value: '#1e3a5a' },
    },
    floorplan: {
      'canvas-bg': { value: '#F4F7FC' },
      'checkpoint-action-hit-area': { value: '{colors.transparent}' },
      'marker-bg': { value: '{colors.white}' },
      'marker-ring': { value: '#0C121D' },
      'marker-glyph': { value: '#0C121D' },
      'cone-fill': { value: '#94A3B8' },
      'cone-fill-focused': { value: '#94A3B8' },
      'cone-edge': { value: '#94A3B8' },
      'edit-frame': { value: '#14B8A6' },
      'delete-zone-idle': { value: '#DC2626' },
      'delete-zone-active': { value: '#DC2626' },
    },
    device: {
      'associated-bg': { value: '#16B3B0' },
      'associated-icon': { value: '#141D2E' },
      'grid-enabled-bg': { value: '#12B779' },
      'grid-enabled-icon': { value: '#1C283F' },
      'tailgate-enabled-bg': { value: '#1FB812' },
      'tailgate-enabled-icon': { value: '#052C02' },
      'ready-to-associate-bg': { value: '#8FB8DC' },
      'ready-to-associate-icon': { value: '{colors.white}' },
    },
    'device-connection': {
      'online-halo': { value: '{colors.green.100}' },
      'warning-halo': { value: '{colors.orange.100}' },
      'error-halo': { value: '{colors.red.100}' },
    },
    interactive: {
      // ─── Nav (always dark — same values as dark mode) ─────────────────────
      'nav-active': { value: '#14B8A6' },
      // ─────────────────────────────────────────────────────────────────────
      primary: { value: '{colors.legacy.teal}' },
      'primary-hover': { value: '#0D9488' },
      'primary-active': { value: '#0F766E' },
      'primary-disabled-bg': { value: '#99F6E4' },
      'primary-disabled-text': { value: '#50CDB9' },
      'secondary-border': { value: '#e6eaed' },
      // Destructive actions. Values from Legacy.tokens.json — the light palette
      // had no danger entries at all, so a danger Button fell through to the
      // dark tokens in both schemes.
      danger: { value: '#DC2626' },
      'danger-hover': { value: '#EF4444' },
      'danger-active': { value: '#B91C1C' },
      'danger-disabled-bg': { value: '#FCA5A5' },
      'danger-disabled-text': { value: '#F87171' },
      'focus-ring': { value: '{colors.dawnBlue.700}' },
      'tertiary-bg': { value: '#FFFFFF' },
      'tertiary-border': { value: '#E2E6EC' },
      'tertiary-text': { value: '#6B7785' },
      'tertiary-hover-bg': { value: '#F5F6F8' },
      'tertiary-hover-border': { value: '#E8EEF2' },
      'tertiary-active-bg': { value: 'transparent' },
      'tertiary-active-border': { value: '#9CA3AF' },
      'tertiary-active-text': { value: '#475569' },
      // Selected-row highlight: very light blue wash.
      'selected-row': { value: '{colors.dawnBlue.50}' },
      // Text-selection (::selection) highlight: dawnBlue wash strong enough
      // to read as selected on white AND on input fills (bg.input #CFD6E2
      // sits at dawnBlue.200's luminance, so this goes one step deeper),
      // light enough that dark text stays legible.
      'text-selection-bg': { value: '{colors.dawnBlue.300}' },
      'text-selection-fg': { value: '{colors.darkBlue.700}' },
      'toggle-on': { value: '{colors.legacy.teal}' },
      // Selected radio ring + dot — Legacy.tokens.json interactive/radio-on
      // aliases {brand.primary} (legacy teal).
      'radio-on': { value: '#14B8A6' },
      // Background fill on Hover state of Icon Button. One tonal step lighter than bg/table-row-hover
      // so kebabs and other Icon Buttons hovered inside hovered rows still register visually.
      'icon-button-hover-bg': { value: '#e2e8f0' },
      // Background fill on Pressed state of Icon Button. One step lighter than hover
      // (darkblue/500 vs darkblue/600) so the press registers as escalation in any row context.
      'icon-button-pressed-bg': { value: '#cbd5e1' },
      // ─── Incident action bar (legacy pre-rebrand blues, not in design token JSON) ──
      'action-bg': { value: '#e3eefb' },
      'action-bg-hover': { value: '#1b253f' },
      'action-bg-selected': { value: '#5f96f5' },
      'action-focus-ring': { value: '#3d7fce' },
    },
    brand: {
      primary: { value: '#14B8A6' },
    },
  },
})
